import { auth } from "../auth";
import { Brand, EmptyState } from "../components/shared";
import { tournamentDateLabel, tournamentStatusCopy } from "../lib/format";
import { memberApi } from "../memberApi";
import {
  CalendarDays,
  ChevronRight,
  DollarSign,
  LogIn,
  MapPin,
  ShieldCheck,
  Trophy,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";

export function TournamentRegistrationScreen({ publicId, account }) {
  const [tournament, setTournament] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
  });

  const load = async () => {
    setStatus((value) => ({ ...value, loading: true, error: "" }));
    try {
      const details = await memberApi.tournament(publicId);
      setTournament(details);
      if (account) {
        const registrations = await memberApi.tournamentRegistrations();
        setRegistration(
          registrations.find(
            (value) => value.publicId.toLowerCase() === publicId.toLowerCase(),
          ) || null,
        );
      }
      setStatus({ loading: false, saving: false, error: "" });
    } catch (error) {
      setStatus({ loading: false, saving: false, error: error.message });
    }
  };

  useEffect(() => {
    load();
  }, [publicId, account]);
  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>OPENING TOURNAMENT</b>
      </div>
    );
  if (!tournament)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <span className="kicker">TOURNAMENT</span>
          <h1>This event is not available.</h1>
          <p>
            {account
              ? status.error ||
                "The link may have expired or the event may be private."
              : "This may be a private organization tournament. Sign in to check your eligibility."}
          </p>
          {!account && (
            <button
              className="action-primary"
              onClick={() => auth.signIn(`/tournaments/${publicId}`)}
            >
              <span>Sign in to check</span>
              <LogIn />
            </button>
          )}
        </section>
      </main>
    );

  const activeRegistration =
    registration && !["Withdrawn", "Rejected"].includes(registration.status);
  const spotsRemaining = Math.max(
    0,
    tournament.capacity - tournament.acceptedCount,
  );
  const save = async (action) => {
    setStatus((value) => ({ ...value, saving: true, error: "" }));
    try {
      const result =
        action === "register"
          ? await memberApi.registerForTournament(publicId)
          : await memberApi.withdrawFromTournament(publicId);
      setRegistration(result);
      setStatus({ loading: false, saving: false, error: "" });
      const details = await memberApi.tournament(publicId);
      setTournament(details);
    } catch (error) {
      setStatus({ loading: false, saving: false, error: error.message });
    }
  };

  return (
    <main className="claim-screen tournament-registration-screen">
      <Brand />
      <section className="claim-card tournament-registration-card">
        <div className="claim-organization">
          <Trophy />
          <span>
            <small>HOSTED BY</small>
            <b>{tournament.organizationName}</b>
          </span>
        </div>
        <span className="kicker">
          {tournament.visibility === "OrganizationOnly"
            ? "PRIVATE ORGANIZATION EVENT"
            : "PUBLIC TOURNAMENT"}
        </span>
        <h1>{tournament.name}</h1>
        {tournament.description && <p>{tournament.description}</p>}
        <div className="tournament-facts">
          <div>
            <CalendarDays />
            <span>
              <small>STARTS</small>
              <b>{tournamentDateLabel(tournament.startsAt)}</b>
            </span>
          </div>
          <div>
            <MapPin />
            <span>
              <small>PLAYING AT</small>
              <b>{tournament.hostPubName || "Venue to be announced"}</b>
              {(tournament.city || tournament.state) && (
                <em>
                  {[tournament.city, tournament.state]
                    .filter(Boolean)
                    .join(", ")}
                </em>
              )}
            </span>
          </div>
          <div>
            <Users />
            <span>
              <small>FIELD</small>
              <b>
                {spotsRemaining > 0
                  ? `${spotsRemaining} of ${tournament.capacity} spots remain`
                  : `${tournament.capacity}-player field is full`}
              </b>
            </span>
          </div>
          <div>
            <DollarSign />
            <span>
              <small>ENTRY</small>
              <b>
                {Number(tournament.entryFee) > 0
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: tournament.currency || "USD",
                    }).format(tournament.entryFee)
                  : "Free entry"}
              </b>
            </span>
          </div>
        </div>
        {activeRegistration ? (
          <div
            className={`tournament-registration-status ${registration.status.toLowerCase()}`}
          >
            <ShieldCheck />
            <span>
              <small>YOUR REGISTRATION</small>
              <b>{tournamentStatusCopy(registration)}</b>
              <em>
                {registration.status === "Pending"
                  ? "The organizer will review your entry."
                  : registration.status === "Accepted"
                    ? "You're in. See you at the oche."
                    : "We'll move you in automatically when a spot opens."}
              </em>
            </span>
          </div>
        ) : !account ? (
          <button
            className="action-primary"
            disabled={!tournament.registrationOpen}
            onClick={() => auth.signIn(`/tournaments/${publicId}`)}
          >
            <span>
              {tournament.registrationOpen
                ? "Sign in to register"
                : "Registration is closed"}
            </span>
            <LogIn />
          </button>
        ) : (
          <button
            className="action-primary"
            disabled={!tournament.registrationOpen || status.saving}
            onClick={() => save("register")}
          >
            <span>
              {status.saving
                ? "Saving your spot…"
                : tournament.requiresOrganizerApproval
                  ? "Request my spot"
                  : "Register for tournament"}
            </span>
            <ChevronRight />
          </button>
        )}
        {activeRegistration && (
          <button
            className="action-quiet tournament-withdraw"
            disabled={status.saving}
            onClick={() => save("withdraw")}
          >
            {status.saving ? "Updating…" : "Withdraw my registration"}
          </button>
        )}
        {!tournament.registrationOpen && !activeRegistration && (
          <p className="tournament-window">
            Registration is not currently open.
          </p>
        )}
        {status.error && (
          <p className="form-error" role="alert">
            {status.error}
          </p>
        )}
      </section>
    </main>
  );
}

export function TournamentRegistrationsView({ registrations, loading, onOpen }) {
  return (
    <section className="section-stack">
      <div className="section-heading">
        <span className="kicker">YOUR EVENTS</span>
        <h1>My tournaments</h1>
        <p>Registration status and tournament-day details in one place.</p>
      </div>
      {loading ? (
        <div className="launch-inline">Loading tournaments…</div>
      ) : registrations.length === 0 ? (
        <EmptyState
          icon={<Trophy />}
          title="No tournaments yet"
          text="When you register through an organizer's tournament link, it will appear here."
        />
      ) : (
        registrations.map((item) => (
          <button
            className="tournament-list-card"
            key={item.id}
            onClick={() => onOpen(item.publicId)}
          >
            <div className="date-tile">
              <b>{new Date(item.startsAt).getDate()}</b>
              <span>
                {new Date(item.startsAt).toLocaleString("en-US", {
                  month: "short",
                })}
              </span>
            </div>
            <span>
              <small>{item.organizationName}</small>
              <b>{item.tournamentName}</b>
              <em>
                <MapPin size={13} />{" "}
                {item.hostPubName || "Venue to be announced"}
              </em>
            </span>
            <strong className={item.status.toLowerCase()}>
              {tournamentStatusCopy(item)}
            </strong>
            <ChevronRight />
          </button>
        ))
      )}
    </section>
  );
}
